import { Bath, Bed, SaveHeart, Share } from "@/components/bnp-icons";
import { getPropertyType } from "@/components/pages/user/dashboard/properties/CreatePropertyForm";
import { capitalizeFirstLetter } from "@/utils/capitalizeFirstLetter";
import {
  LandTitleDocument,
  LandTitleDocumentLabels,
} from "@/types/property-config";
import { Property } from "@/types/property";

interface PropDetailsSemanticsProps {
  property: Property;
  isSaved: boolean;
  onShare: () => void;
  onSave: () => void;
}

const PropDetailsSemantics = ({
  property,
  isSaved,
  onShare,
  onSave,
}: PropDetailsSemanticsProps) => {
  const formatListingType = (listingType?: string) => {
    if (!listingType) return "";
    if (listingType === "sale") return "For Sale";
    if (listingType === "rent") return "For Rent";
    if (listingType === "shortlet") return "Shortlet";
    return "Land";
  };

  const formatTitleDocument = (value: string) => {
    const landLabel = LandTitleDocumentLabels[value as LandTitleDocument];
    if (landLabel) return landLabel;
    return value
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const isSale = property?.listing_type === "sale";
  const titleDocument =
    isSale &&
    ((property as any)?.sale_document_details || property?.land_title_document)
      ? (((property as any)?.sale_document_details ||
          property?.land_title_document) as string)
      : undefined;

  return (
    <>
      {/* LISTING TYPE & PROPERTY TYPE BADGES */}
      <div className="flex flex-wrap items-center gap-2 mt-6">
        {property?.listing_type && (
          <span className="px-3 py-1 text-xs font-semibold rounded-full bg-bnp-cyan text-white capitalize">
            {formatListingType(property.listing_type)}
          </span>
        )}
        {property?.property_type && (
          <span className="px-3 py-1 text-xs font-semibold rounded-full bg-weak-100 text-main-900 border border-soft-200">
            {getPropertyType(property.property_type) ||
              capitalizeFirstLetter(
                property.property_type?.replace(/-/g, " "),
                2,
              )}
          </span>
        )}
        {property?.property_stage && (
          <span className="px-3 py-1 text-xs font-semibold rounded-full bg-weak-100 text-grey-7 border border-soft-200 capitalize">
            {property.property_stage}
          </span>
        )}
        {titleDocument && (
          <span className="px-3 py-1 text-xs font-semibold rounded-full bg-weak-100 text-main-900 border border-soft-200">
            Title Document: {formatTitleDocument(titleDocument)}
          </span>
        )}
      </div>

      {/* BEDROOMS / BATHROOMS / TAGS + SHARE & SAVE */}
      <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-sm font-bold text-soft-400 mt-4 border-b border-soft-200 pb-4">
        {property?.bedroom_count != null && (
          <div className="flex items-center gap-1 sm:gap-2">
            <Bed />
            <span className="whitespace-nowrap">
              {property.bedroom_count}{" "}
              {property.bedroom_count === 1 ? "Bed" : "Beds"}
            </span>
          </div>
        )}
        {property?.bathroom_count != null && (
          <>
            {property?.bedroom_count != null && (
              <div className="border-r border-grey-5 h-4" />
            )}
            <div className="flex items-center gap-1 sm:gap-2">
              <Bath />
              <span className="whitespace-nowrap">
                {property.bathroom_count}{" "}
                {property.bathroom_count === 1 ? "Bath" : "Baths"}
              </span>
            </div>
          </>
        )}
        {property?.toilet != null && (
          <>
            {property?.toilet != null && (
              <div className="border-r border-grey-5 h-4" />
            )}
            <div className="flex items-center gap-1 sm:gap-2">
              <Bath />
              <span className="whitespace-nowrap">
                {property.toilet} {property.toilet === 1 ? "Toilet" : "Toilets"}
              </span>
            </div>
          </>
        )}
        {property?.serviced && (
          <>
            <div className="border-r border-grey-5 h-4" />
            <span className="whitespace-nowrap">Serviced</span>
          </>
        )}
        {property?.shared && (
          <>
            <div className="border-r border-grey-5 h-4" />
            <span className="whitespace-nowrap">Shared</span>
          </>
        )}
        {property?.furnished && (
          <>
            <div className="border-r border-grey-5 h-4" />
            <span className="whitespace-nowrap">Furnished</span>
          </>
        )}

        {/* SHARE & SAVE */}
        <div className="flex items-center gap-2 sm:gap-4 sm:ml-auto shrink-0">
          <button
            onClick={onShare}
            className="flex items-center gap-1 text-sm text-grey-7 hover:text-main-900 transition-colors"
            aria-label="Share property"
          >
            <span>Share</span>
            <Share size={18} />
          </button>
          <button
            onClick={onSave}
            className="flex items-center gap-1 text-sm text-grey-7 hover:text-main-900 transition-colors"
            aria-label="Save property"
          >
            <span>Save</span>
            <SaveHeart saved={isSaved} size={18} />
          </button>
        </div>
      </div>
    </>
  );
};

export default PropDetailsSemantics;
